import React from "react";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { CalendarIcon, X } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/**
 * Date picker con Calendar de shadcn en formato es-ES (dd/mm/aaaa).
 * value/onChange usan ISO 'YYYY-MM-DD' para máxima interoperabilidad con la API.
 * Ancho fijo para que dd/mm/aaaa + aspa no se recorten.
 */
export default function DatePicker({ value, onChange, placeholder = "dd/mm/aaaa", testid, id, label, "aria-label": ariaLabel }) {
  const [open, setOpen] = React.useState(false);
  const selected = value ? parseISO(value) : undefined;
  const shown = value ? format(selected, "dd/MM/yyyy", { locale: es }) : placeholder;
  const name = ariaLabel || label || (value ? `Fecha ${shown}` : placeholder);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          data-testid={testid}
          aria-label={name}
          aria-haspopup="dialog"
          aria-expanded={open}
          className={cn(
            "relative inline-flex h-9 w-[13.75rem] min-w-[13.75rem] max-w-[13.75rem] items-center rounded-md border border-input bg-background px-2.5 pr-8 text-left text-sm shadow-sm",
            "hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
            !value && "text-slate-500",
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4 shrink-0 text-slate-500" />
          <span className="min-w-[10.5ch] shrink-0 whitespace-nowrap tabular-nums">
            {shown}
          </span>
          {value ? (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onChange(""); }}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); onChange(""); } }}
              data-testid={testid ? `${testid}-clear` : undefined}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-slate-400 hover:text-slate-700"
              aria-label="Limpiar fecha"
            >
              <X className="h-3.5 w-3.5" />
            </span>
          ) : null}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 z-[2000]" align="start" sideOffset={6} collisionPadding={12}>
        <Calendar
          mode="single"
          locale={es}
          selected={selected}
          onSelect={(d) => {
            if (d) onChange(format(d, "yyyy-MM-dd"));
            setOpen(false);
          }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}
